import { FunctionComponent, memo, useEffect, useState, useMemo } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { IPracticeExam } from "./IPracticeExam";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addPracticeExam } from "../../Redux/Slices/PracticeExam/AddPracticeExamSlice";
import { EditPracticeExam } from "../../Redux/Slices/PracticeExam/EditPracticeExamSlice";
import { listQuestionMapping } from "../../Redux/Slices/Questions/QuestionMappingSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import TextAreaInputField from "../../Components/TextAreaInput/TextAreaInputField";
import {
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import { listQuestionSubType } from "../../Redux/Slices/QuestionSubType/ListQuestionSubTypeSlice";
interface PracticeExamFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: IPracticeExam;
  setInitialValues: React.Dispatch<React.SetStateAction<IPracticeExam>>;
}

const PracticeExamForm: FunctionComponent<PracticeExamFormProps> = (props) => {
  const dispatch = useAppDispatch();

  // Fetching question types from Redux

  const { data: listPracticeExams } = useAppSelector(
    (state) => state.ListPracticeExamSlice
  );

  const PracticeExam = Array.isArray(listPracticeExams?.data)
    ? listPracticeExams?.data.map((user, index) => ({
      ...user,
      index: index + 1,
    }))
    : [];

  const { data: questionMappingData } = useAppSelector(
    (state) => state.QuestionMappingSlice
  );

  const { data: listQuestions } = useAppSelector(
    (state) => state.ListQuestionTypeSlice
  );
  const QuestionList = Array.isArray(listQuestions?.data)
    ? listQuestions?.data
      ?.filter((role) => role.Status !== false)
      .map((listQuestions) => ({
        label: listQuestions?.name!,
        value: listQuestions.id,
      }))
    : [];

  const filteredQuestionTypeList = QuestionList?.filter(
    (questionType) =>
      !PracticeExam?.some(
        (item) => item?.question_type_id === questionType?.value
      )
  );

  // Map filtered list to required format
  const QuestionFInalList = filteredQuestionTypeList?.map((questionType) => ({
    label: questionType.label!,
    value: questionType.value,
  }));

  const userSchema = z.object({
    id: z.number().optional(),
    name: z
      .string({ message: "Please enter exam name" })
      .min(1, { message: "Please enter exam name" })
      .max(100, { message: "name cannot exceed 100 characters" }),
    question_type_id: z.number({ message: "Please select question type" }),
    question_count: z
      .number({ message: "Please enter question counts" })
      .min(1, { message: "Please enter question count" })
      .max(1000, { message: "count cannot exceed 1000 " }),
    description: z
      .string({ message: "Please enter description" })
      .min(1, { message: "Please enter description" })
      .max(500, { message: "description cannot exceed 500 characters" }),
    status: z.boolean({
      message: "Please select the status",
    }),
    duration: z
      .number({ message: "Please enter the duration of the exam" })
      .refine((val) => val > 0, {
        message: "PracticeExam duration must be greater than 0 mintues",
      }),
    instruction: z
      .array(z.string().min(1).max(250))
      .min(1, "Add at least one instruction")
      .max(20, "Cannot add more than 20 instructions"),

    subtype_id: z.any().optional(),
    sub_question_count: z.any().optional(),
    sequence: z.any(),
    question_selection_type: z.enum(['random', 'predefined']).optional(),
    question_mappings: z.array(z.number()).optional(),
  });

  const methods = useForm<IPracticeExam>({
    defaultValues: {
      ...props.initialValues,
      instruction: props.initialValues.instruction ?? [],
      status: props.initialValues.id ? props.initialValues.status : true,
      question_selection_type: props.initialValues?.question_selection_type || "predefined",
      question_mappings: props.initialValues?.question_mappings || [],
    },
    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  useEffect(() => {
    return () => {
      props.setInitialValues({});
    };
  }, [dispatch, props]);

  // Status list
  const PracticeExamSatus = [
    {
      label: "Active",
      value: true,
    },

    {
      label: "Inactive",
      value: false,
    },
  ];

  const { setValue, getValues } = methods;
  const [instructionInput, setInstructionInput] = useState<string>("");
  // const [instructions, setInstructions] = useState<string[]>([]); // Removing unused state
  const [instructionError, setInstructionError] = useState("");
  const [selectedQuestions, setSelectedQuestions] = useState<AutocompleteOption[]>([]);

  // Watch values for effect dependencies
  const watchedQuestionTypeId = methods.watch("question_type_id");
  const watchedSelectionType = methods.watch("question_selection_type");

  // Effect to handle question mapping fetch and clear
  useEffect(() => {
    // Clear selection when type changes

    if (watchedQuestionTypeId && watchedSelectionType === 'predefined') {
      dispatch(listQuestionMapping({
        question_type_id: watchedQuestionTypeId,
        page: 1,
        page_size: 1000
      }));
    }
  }, [watchedQuestionTypeId, watchedSelectionType, dispatch]);

  const questionMappingOptions = useMemo(() => {
    return Array.isArray(questionMappingData?.data?.questions)
      ? questionMappingData?.data?.questions.map((q: any) => ({
        label: q.question,
        value: q.id
      }))
      : [];
  }, [questionMappingData]);

  // Sync initial values for edit mode or when selection changes
  // Actually, we need to bind the Select to methods.watch or maintain local state and sync.
  // Using local state for the Select value is easier for formatted options, then setValue on change.
  // But we need to load initial values if editing.
  useEffect(() => {
    if (props.initialValues.question_mappings && (questionMappingOptions?.length ?? 0) > 0) {
      // Map IDs to options
      const initialIds = props.initialValues.question_mappings;
      const initialOptions = questionMappingOptions?.filter((opt: any) => initialIds.includes(opt.value));
      setSelectedQuestions(initialOptions || []);
    }
  }, [props.initialValues.question_mappings, questionMappingOptions]);

  const handleAddInstruction = () => {
    if (instructionError) return;

    const currentInstructions = getValues("instruction") || [];

    if (instructionInput.trim() && currentInstructions.length < 20) {
      const updatedInstructions = [...currentInstructions, instructionInput];
      // setInstructions(updatedInstructions);
      setValue("instruction", updatedInstructions, { shouldValidate: true });
      setInstructionInput("");
    }
  };

  // Delete instruction handler
  const handleDeleteInstruction = (index: number) => {
    const currentInstructions = getValues("instruction");
    const updatedInstructions =
      currentInstructions && currentInstructions?.filter((_, i) => i !== index);
    // setInstructions(updatedInstructions!); // Update local state
    setValue("instruction", updatedInstructions); // Update instructions field
    methods.trigger("instruction"); // Force re-render to reflect changes
  };

  const { data: listQuestionssub } = useAppSelector(
    (state) => state.ListQuestionSubTypeSlice
  );
  const QuestionsubList = Array.isArray(listQuestionssub?.data)
    ? listQuestionssub?.data
      ?.filter((role) => role.status !== false) // Filter out roles where status is false
      .map((listQuestions) => ({
        label: listQuestions?.subtype_name!,
        value: listQuestions.id,
      }))
    : [];
  const [displayDataListsub, setDisplayDataListsub] = useState<
    {
      subtype_id?: { label: string; value: number } | null;
      sub_question_count?: number | null;
      sequence?: number | null;
    }[]
  >([]);
  const questionTypessub = methods.getValues("subtype_id");
  const isDuplicatesub = displayDataListsub.some(
    (item) => item.subtype_id?.value === questionTypessub
  );

  const handleAddDatasub = () => {
    const subtypeId = methods.getValues("subtype_id"); // Get selected subtype value
    const subtypeCount = methods.getValues("sub_question_count");
    const subtypeSequence = methods.getValues("sequence");

    if (!subtypeId || !subtypeCount) {
      return;
    }

    const selectedSubtype = QuestionsubList?.find(
      (list) => list.value === subtypeId
    );

    if (!selectedSubtype) {
      return;
    }

    // Check if already exists in displayDataListsub
    setDisplayDataListsub((prevList: any) => {
      const isDuplicate = prevList.some(
        (item: any) => item.subtype_id?.value === subtypeId
      );

      if (isDuplicate) {
        return prevList; // Return existing state, preventing update
      }

      return [
        ...prevList,
        {
          subtype_id: selectedSubtype, // Corrected key
          sub_question_count: subtypeCount, // Corrected key
          sequence: subtypeSequence, // Ensure correct property
        },
      ];
    });

    // Reset fields
    methods.resetField("subtype_id");
    methods.resetField("sub_question_count", { defaultValue: "" });
    methods.resetField("sequence", { defaultValue: "" });
  };

  // Delete data by index
  const handleDeleteDatasub = (index: number) => {
    setDisplayDataListsub((prevList) => prevList.filter((_, i) => i !== index));
  };
  const questionCount: any = methods.getValues("question_count");

  const totalSubQuestionCount = displayDataListsub?.reduce(
    (total, item: any) => total + item?.sub_question_count,
    0
  );

  useEffect(() => {
    if (methods.getValues("id")) {
      const subtypes: any = methods.getValues("subtype_data");

      // Transform subtype_data
      const updatedSubtypeData = subtypes?.map((sub: any) => {
        const matchingSubtype: any = QuestionsubList?.find((d: any) => {
          return d.value === sub.subtype_id;
        });

        return {
          subtype_id: {
            label: matchingSubtype?.label || "Unknown",
            value: sub.subtype_id,
          },
          sub_question_count: sub.sub_question_count,
          sequence: sub.sequence,
        };
      });

      setDisplayDataListsub(updatedSubtypeData);
    }
  }, [methods, QuestionsubList]); // Dependency array (adjust as needed)

  const onSubmit: SubmitHandler<IPracticeExam> = (val) => {
    const examData = {
      ...(val.id && { id: val.id }),
      name: val.name,
      question_type_id: val.question_type_id,
      duration: val.duration,
      question_count: val.question_count,
      description: val.description,
      instruction: val.instruction,
      status: val.status,
      subtype_data: displayDataListsub.length
        ? displayDataListsub.map((qt: any) => ({
          subtype_id: qt?.subtype_id?.value,
          sub_question_count: qt?.sub_question_count,
          sequence: qt?.sequence,
        }))
        : [],

      question_selection_type: val.question_selection_type,
      question_mappings: val.question_selection_type === 'predefined' ? selectedQuestions.map(q => q.value) : [],
    };

    if (
      displayDataListsub.length >= 1 &&
      totalSubQuestionCount === questionCount
    ) {
      val.id
        ? dispatch(EditPracticeExam(examData))
        : dispatch(addPracticeExam(examData));
    } else if (displayDataListsub.length === 0) {
      val.id
        ? dispatch(EditPracticeExam(examData))
        : dispatch(addPracticeExam(examData));
    }
  };
  const FIlterdata: any = QuestionFInalList?.filter((data) => {
    return data?.value === methods.getValues("question_type_id");
  });

  const instructionList = methods.watch("instruction");

  console.log("Instruction length:", instructionList?.length);
  return (
    <FormProvider {...methods}>
      <form ref={props.formRef} onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="form_double_input">
          <TextInputFieldC name="name" label="Practice Exam Name" type="text" />

          {/* Question Selection Type */}
          <Box sx={{ mb: 2, gridColumn: "span 2" }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Question Selection Type
            </Typography>
            <RadioGroup
              row
              value={methods.watch("question_selection_type")}
              onChange={(e) => {
                methods.setValue("question_selection_type", e.target.value as 'random' | 'predefined', { shouldValidate: true });
                if (e.target.value === 'random') {
                  setSelectedQuestions([]);
                }
              }}
            >
              <FormControlLabel
                value="random"
                control={<Radio />}
                label="Random Questions"
              />
              <FormControlLabel
                value="predefined"
                control={<Radio />}
                label="Predefined Questions"
              />
            </RadioGroup>
          </Box>

          <Select
            name="question_type_id"
            label="Question Type"
            options={QuestionFInalList ?? []}
            value={
              QuestionList?.find(
                (list) => list.value === methods.getValues("question_type_id")
              ) ?? null
            }
            onChange={(_, value) => {
              const val = value as AutocompleteOption;
              methods.setValue("question_type_id", val?.value, {
                shouldValidate: true,
              });
              dispatch(listQuestionSubType({ questiontype_id: val?.value }));
              setDisplayDataListsub([]);
            }}
          />

          {/* Question Dropdown - Only show if predefined */}
          {methods.watch("question_selection_type") === 'predefined' && (
            <div style={{ width: "100%" }}>
              <Select
                name="question_mappings"
                label="Select Questions"
                multiple
                options={questionMappingOptions}
                value={selectedQuestions}
                onChange={(_, value) => {
                  setSelectedQuestions(value as AutocompleteOption[]);
                  // Update form value too if needed, or just handle at submit
                  // methods.setValue("question_mappings", value.map(v => v.value));
                }}
              />
            </div>
          )}

          <NumberInputField
            type="number"
            name="question_count"
            label="Question Count"
            inputProps={{ max: FIlterdata[0]?.label === "Writing" ? 1 : 100 }}
          />
          <TextAreaInputField
            name="description"
            label="description"
            type="text"
          />

          <NumberInputField
            name="duration"
            label="Duration in Mintues"
            type="number"
            sx={{ width: "100%" }}
          />

          <Select
            name="status"
            label="Status"
            options={PracticeExamSatus}
            value={
              PracticeExamSatus?.find(
                (role) => role.value === methods.getValues("status")
              ) ?? null
            }
            onChange={(_, value) => {
              const val = value as AutocompleteOption;
              methods.setValue("status", val?.value, { shouldValidate: true });
            }}
          />
          {/* Instruction Input Field with TextArea */}
          <Box display="flex">
            <TextField
              label="Instruction"
              value={instructionInput}
              onChange={(e) => {
                const value = e.target.value;

                // live validation
                if (value.length > 250) {
                  setInstructionError(
                    "Instruction cannot exceed 250 characters"
                  );
                } else if (value.trim().length === 0) {
                  setInstructionError("Please enter the instruction");
                } else {
                  setInstructionError("");
                }

                setInstructionInput(value);
              }}
              fullWidth
              multiline
              error={instructionError !== ""}
              helperText={instructionError}
              minRows={1}
              maxRows={4}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleAddInstruction();
                methods.trigger("instruction");
              }}
              sx={{
                ml: 1,
                width: "10px",
                height: "4rem",
                bgcolor: "primary.main", // Set the background color to primary
                ":hover": {
                  bgcolor: "primary.main", // Keep the hover background color the same
                },
              }}
            >
              <AddIcon sx={{ color: "white" }} />
            </Button>
          </Box>
          {/* Render Instructions */}
          {instructionList?.map((instruction, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              bgcolor="lightgrey"
              p={0.3}
              my={1}
              borderRadius="4px"
              sx={{
                marginBottom: "0px",
                marginTop: "0px",

                ":hover button": { visibility: "visible" }, // Visibility for smoother transitions
              }}
            >
              <Typography
                sx={{
                  flexGrow: 1, // Make text take available space
                  wordWrap: "break-word", // Ensure long text wraps
                  overflow: "hidden",
                  padding: "10px",
                }}
              >
                {instruction}
              </Typography>
              <IconButton onClick={() => handleDeleteInstruction(index)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </div>
        {methods.formState.errors.instruction && (
          <Typography color="error" sx={{ mt: 1 }}>
            {methods.formState.errors.instruction.message}
          </Typography>
        )}
        <div className="twofieldlist" style={{ marginTop: "20px" }}>
          {/* Select for Question Type */}
          <Select
            name="subtype_id"
            label="Question Subtype"
            sx={{ width: "50%" }}
            options={QuestionsubList ?? []}
            disabled={(QuestionsubList?.length ?? 0) < 1}
            value={
              QuestionsubList?.find(
                (list) => list.value === methods.getValues("subtype_id")
              ) ?? null
            }
            onChange={(_, value) => {
              const val = value as AutocompleteOption;
              methods.setValue("subtype_id", val?.value, {
                shouldValidate: true,
              });
            }}
          />

          {/* Input for Question Counts */}
          <NumberInputField
            type="number"
            disabled={(QuestionsubList?.length ?? 0) < 1}
            sx={{ width: "50%" }}
            name="sub_question_count"
            label="Question Subcount"
          />

          <NumberInputField
            type="number"
            disabled={(QuestionsubList?.length ?? 0) < 1}
            sx={{ width: "50%" }}
            name="sequence"
            label="Sequence"
          />
          {(QuestionsubList?.length ?? 0) < 1 ? (
            <AddCircleOutlineIcon sx={{ fontSize: "30px", color: "gray" }} />
          ) : (
            <AddCircleOutlineIcon
              titleAccess={
                isDuplicatesub === true
                  ? "Question Subtype already exists .Please add another Question Subtype"
                  : ""
              }
              sx={{ fontSize: "30px", color: "#009DFF", cursor: "pointer" }}
              onClick={handleAddDatasub}
            />
          )}
        </div>
      </form>
      {displayDataListsub.length > 0 && (
        <div
          style={{
            marginTop: "20px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(45%, 1fr))", // 2 columns
            gap: "10px",
          }}
        >
          {displayDataListsub.map((data, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
                backgroundColor: "lightgrey",
                borderRadius: "5px",
                padding: "5px",
                // width: "50%",
                boxShadow: "0px 3px 9px darkgray",
              }}
            >
              <div>
                <div>
                  <b>Question Subtype :</b> {data?.subtype_id?.label}
                </div>
                <div>
                  <b>Question Subcount :</b> {data?.sub_question_count}
                </div>
                <div>
                  <b>Sequence :</b> {data?.sequence}{" "}
                </div>
              </div>

              {/* Delete button for each entry */}
              <IconButton onClick={() => handleDeleteDatasub(index)}>
                <DeleteIcon color="error" />
              </IconButton>
            </div>
          ))}
        </div>
      )}
      <div>
        {totalSubQuestionCount !== 0 ? (
          totalSubQuestionCount === questionCount ? (
            ""
          ) : totalSubQuestionCount > questionCount ? (
            <p style={{ color: "red" }}>
              Total question count across subtypes must match the required count
              for the question type. Please remove{" "}
              {totalSubQuestionCount - questionCount} to align.
            </p>
          ) : (
            <p style={{ color: "red" }}>
              Total question count across subtypes must match the required count
              for the question type. Please add{" "}
              {questionCount - totalSubQuestionCount} more.
            </p>
          )
        ) : null}
      </div>
    </FormProvider>
  );
};

export default memo(PracticeExamForm);
